import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/EndOfDayClosure.jsx
// Branch-driven EOD workflow.
// States: NO_DAY/OPEN → PENDING_CASH_VERIFICATION → PENDING_CHECKER_APPROVAL → (CLOSED via Phase 7)

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Typography, Spin, Alert, Row, Col, Statistic,
  Space, DatePicker, Select, Tag, Divider, Steps, Result,
  Modal, Input, List, message,
} from 'antd';
import {
  PlayCircleOutlined, SaveOutlined, CheckOutlined,
  ClockCircleOutlined, LockOutlined, ReloadOutlined,
  UnlockOutlined, WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { initiateEod, submitEodDenomination, submitEodForApproval, fetchDayStatus, fetchBranchEodVisibility, fetchBlockingDays, forceCloseDay, forceCloseAllDays } from '../api';
import DenominationCountForm from '../components/DenominationCountForm';
import { DAY_STATUS } from '../constants/treasuryConstants';

const { Title, Text, Paragraph } = Typography;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const DAY_STEP = {
  NO_DAY:                       0,
  [DAY_STATUS.OPEN]:            0,
  [DAY_STATUS.REOPENED]:        0,
  [DAY_STATUS.PENDING_CASH_VERIFICATION]:   1,
  [DAY_STATUS.PENDING_CHECKER_APPROVAL]:    2,
  [DAY_STATUS.CLOSED]:          3,
};

export default function EndOfDayClosure() {
  const { user } = useAuth();

  const [isHO, setIsHO] = useState(false);
  const [userBranch, setUserBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const [eodState, setEodState] = useState(null);   // { dayStatus, businessDay, summary }
  const [counts, setCounts] = useState({});

  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [eodVisibility, setEodVisibility] = useState({});

  // ── Blocking previous days + force-close override ──────────────────────────
  const [blockingDays, setBlockingDays] = useState([]);
  const [canOverride, setCanOverride] = useState(false);
  const [forceDay, setForceDay] = useState(null);        // day object being force-closed
  const [forceCounts, setForceCounts] = useState({});
  const [forceReason, setForceReason] = useState('');
  const [forceLoading, setForceLoading] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);       // closing all previous days at once
  const [messageApi, msgContext] = message.useMessage();

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        setUserBranch(branch);

        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);

        if (ho) {
          const bRes = await fetchBranches({ limit: 100 });
          const list = bRes?.data?.data || bRes?.data || [];
          setBranches(list);
        } else {
          setSelectedBranchId(user.branch);
        }
      } catch {
        setError('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  // ── Fetch EOD state ────────────────────────────────────────────────────────
  // opts.skipCountsReset — don't overwrite user's in-progress denomination input
  // opts.silent          — skip the loading spinner (used for background polls)
  const fetchEodState = useCallback(async (opts = {}) => {
    const { skipCountsReset = false, silent = false } = opts;
    const branchId = selectedBranchId || (!isHO ? user?.branch : null);
    if (!branchId) { setEodState(null); return; }

    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = {
        businessDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      };
      // Use day-status endpoint which returns per-branch status with summary
      const res = await fetchDayStatus({ ...params, branchId });
      const rows = res?.data?.data || [];
      const row = rows.find(r => r.branch?._id === branchId || r.branch?._id?.toString() === branchId) || rows[0];

      if (row) {
        setEodState({
          dayStatus: row.dayStatus,
          businessDay: row.businessDay,
          summary: row.summary,
          pendingApproval: row.pendingApproval || null,
        });
        setLastUpdated(new Date());
        if (!skipCountsReset) {
          // Pre-fill denomination counts
          if (row.businessDay?.denominationCount && row.businessDay.isDenominationSaved) {
            // Use saved counts if they explicitly saved them
            const d = row.businessDay.denominationCount;
            if (d.totalPhysicalCash > 0) {
              setCounts({
                note2000: d.note2000, note500: d.note500, note200: d.note200,
                note100: d.note100, note50: d.note50, note20: d.note20,
                note10: d.note10, note5: d.note5, note2: d.note2, note1: d.note1,
              });
            }
          } else if (row.summary?.expectedDenominations) {
            // Otherwise, pre-fill with the expected baseline
            const d = row.summary.expectedDenominations;
            if (d.totalPhysicalCash > 0) {
              setCounts({
                note2000: d.note2000, note500: d.note500, note200: d.note200,
                note100: d.note100, note50: d.note50, note20: d.note20,
                note10: d.note10, note5: d.note5, note2: d.note2, note1: d.note1,
              });
            } else {
              setCounts({});
            }
          } else {
            setCounts({});
          }
        }
      } else {
        setEodState({ dayStatus: 'NO_DAY', businessDay: null, summary: { openingCash: 0, totalCashIn: 0, totalCashOut: 0, closingBalance: 0 } });
        if (!skipCountsReset) setCounts({});
      }
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load EOD state');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedBranchId, selectedDate, isHO, user]);

  // ── Blocking previous days ──────────────────────────────────────────────────
  const loadBlockingDays = useCallback(async () => {
    const branchId = selectedBranchId || (!isHO ? user?.branch : null);
    if (!branchId) { setBlockingDays([]); setCanOverride(false); return; }
    try {
      const res = await fetchBlockingDays(branchId);
      const data = res?.data?.data || {};
      setBlockingDays(data.days || []);
      setCanOverride(!!data.canOverride);
    } catch {
      setBlockingDays([]);
      setCanOverride(false);
    }
  }, [selectedBranchId, isHO, user]);

  useEffect(() => {
    if (!initLoading) { fetchEodState(); loadBlockingDays(); }
  }, [selectedBranchId, selectedDate, initLoading]);

  // Poll every 30 s while awaiting cash verification so new branch funding is reflected
  // without resetting denomination counts the user is actively entering.
  const isPendingVerificationForPoll = eodState?.dayStatus === DAY_STATUS.PENDING_CASH_VERIFICATION;
  useEffect(() => {
    if (!isPendingVerificationForPoll) return;
    const interval = setInterval(
      () => fetchEodState({ skipCountsReset: true, silent: true }),
      30000,
    );
    return () => clearInterval(interval);
  }, [isPendingVerificationForPoll, fetchEodState]);

  // Fetch EOD display visibility settings whenever the active branch changes
  useEffect(() => {
    const branchId = selectedBranchId || (!isHO ? user?.branch : null);
    if (!branchId || initLoading) { setEodVisibility({}); return; }
    fetchBranchEodVisibility(branchId)
      .then(res => setEodVisibility(res?.data?.data || {}))
      .catch(() => {});
  }, [selectedBranchId, isHO, user, initLoading]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleAction = async (fn, successText) => {
    const branchId = selectedBranchId || user?.branch;
    const businessDate = selectedDate ? selectedDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    setActionLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await fn({ branchId, businessDate });
      setSuccessMsg(successText);
      await fetchEodState();
      await loadBlockingDays();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleInitiate = () => handleAction(
    (d) => initiateEod(d),
    'EOD initiated — enter denomination counts below',
  );

  const handleSaveDenomination = () => handleAction(
    (d) => submitEodDenomination({ ...d, counts }),
    'Denomination counts saved',
  );

  const handleSubmit = () => handleAction(
    (d) => submitEodForApproval(d),
    'Day submitted to HO for approval',
  );

  // ── Force-close a stuck previous day (override users only) ─────────────────
  const prefillFromExpected = (day) => {
    const d = day?.expectedDenominations || {};
    if (d.totalPhysicalCash > 0) {
      setForceCounts({
        note2000: d.note2000, note500: d.note500, note200: d.note200,
        note100: d.note100, note50: d.note50, note20: d.note20,
        note10: d.note10, note5: d.note5, note2: d.note2, note1: d.note1,
      });
    } else {
      setForceCounts({});
    }
  };

  const openForceModal = (day) => {
    setBulkMode(false);
    setForceDay(day);
    setForceReason('');
    prefillFromExpected(day);
  };

  // Close ALL previous days: operator counts the LATEST day; earlier days auto-balance
  const openBulkModal = () => {
    const latest = blockingDays[blockingDays.length - 1];
    if (!latest) return;
    setBulkMode(true);
    setForceDay(latest);
    setForceReason('');
    prefillFromExpected(latest);
  };

  const closeForceModal = () => {
    setForceDay(null);
    setForceCounts({});
    setForceReason('');
    setBulkMode(false);
  };

  const forcePhysical = Object.entries(forceCounts).reduce((sum, [key, qty]) => {
    const note = { note2000: 2000, note500: 500, note200: 200, note100: 100, note50: 50, note20: 20, note10: 10, note5: 5, note2: 2, note1: 1 };
    return sum + (qty || 0) * (note[key] || 0);
  }, 0);
  const forceExpected = forceDay?.expectedCash || 0;
  const forceDifference = parseFloat((forcePhysical - forceExpected).toFixed(2));

  const handleForceClose = async () => {
    if (!forceDay) return;
    if (!forceReason.trim()) {
      messageApi.warning('Please enter a reason for the override.');
      return;
    }
    const branchId = selectedBranchId || user?.branch;
    setForceLoading(true);
    try {
      const res = bulkMode
        ? await forceCloseAllDays({
            branchId,
            counts: forceCounts,
            reason: forceReason.trim(),
          })
        : await forceCloseDay({
            branchId,
            businessDate: dayjs(forceDay.businessDate).format('YYYY-MM-DD'),
            counts: forceCounts,
            reason: forceReason.trim(),
          });
      messageApi.success(res?.data?.message || 'Previous day closed');
      closeForceModal();
      await loadBlockingDays();
      await fetchEodState();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || err.message || 'Failed to close day');
    } finally {
      setForceLoading(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const totalPhysical = Object.entries(counts).reduce((sum, [key, qty]) => {
    const note = { note2000: 2000, note500: 500, note200: 200, note100: 100, note50: 50, note20: 20, note10: 10, note5: 5, note2: 2, note1: 1 };
    return sum + (qty || 0) * (note[key] || 0);
  }, 0);

  const expectedCash = eodState?.summary?.closingBalance || 0;
  const difference = parseFloat((totalPhysical - expectedCash).toFixed(2));
  // A day with ₹0 expected closing cash legitimately closes with all-zero
  // counts, so don't require a nonzero note — just that the form was either
  // touched or there's nothing to count in the first place.
  const denominationEntered = expectedCash === 0 || Object.keys(counts).length > 0;
  const canSubmit = denominationEntered && difference === 0;

  const dayStatus = eodState?.dayStatus || 'NO_DAY';
  const currentStep = DAY_STEP[dayStatus] ?? 0;
  const isPendingVerification = dayStatus === DAY_STATUS.PENDING_CASH_VERIFICATION;
  const isEditable = isPendingVerification;

  // HO always sees all fields; visibility toggles only apply to branch users
  const showExpectedClosingCash = isHO || !eodVisibility.hideExpectedClosingCash;
  const formHideExpectedCash    = !isHO && !!eodVisibility.hideExpectedCash;
  const formHideDifference      = !isHO && !!eodVisibility.hideDifference;

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  const activeBranchId = selectedBranchId || (!isHO ? user?.branch : null);

  return (
    <div style={{ padding: '16px 0' }}>
      {msgContext}
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>End of Day Closure</Title>
          {userBranch && !isHO && (
            <Text type="secondary" style={{ fontSize: 12 }}>{userBranch.name} ({userBranch.code})</Text>
          )}
        </Col>
        <Col>
          <Space>
            {isHO && (
              <Select
                placeholder="Select branch"
                showSearch
                allowClear
                style={{ width: 220 }}
                value={selectedBranchId}
                onChange={setSelectedBranchId}
                optionFilterProp="label"
                options={branches.map(b => ({ value: b._id, label: `${b.name} (${b.code})` }))}
              />
            )}
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY"
              allowClear={false}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchEodState} loading={loading} />
          </Space>
        </Col>
      </Row>

      {isHO && !selectedBranchId && (
        <Alert type="info" message="Select a branch to manage its EOD closure." style={{ marginBottom: 16 }} />
      )}

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}
      {successMsg && <Alert type="success" message={successMsg} closable onClose={() => setSuccessMsg(null)} style={{ marginBottom: 16 }} />}

      {/* ── Blocking previous days (unclosed days that block today) ── */}
      {activeBranchId && blockingDays.length > 0 && (
        <Card
          size="small"
          style={{ marginBottom: 16, borderColor: '#faad14' }}
          title={
            <Space>
              <WarningOutlined style={{ color: '#faad14' }} />
              <span>{blockingDays.length} previous day{blockingDays.length > 1 ? 's are' : ' is'} still open</span>
            </Space>
          }
          extra={
            canOverride && blockingDays.length > 1 ? (
              <Button type="primary" icon={<UnlockOutlined />} onClick={openBulkModal}>
                Close all {blockingDays.length} days
              </Button>
            ) : null
          }
        >
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
            These earlier business days were never closed, which blocks new transactions and
            opening today.{' '}
            {canOverride
              ? 'As an authorized override user you can close each one below — enter the physical denomination count and confirm.'
              : 'Only an authorized override user (configured by HO in Treasury Settings) can close them.'}
          </Paragraph>
          <List
            size="small"
            dataSource={blockingDays}
            renderItem={(d) => (
              <List.Item
                actions={[
                  canOverride ? (
                    <Button
                      key="close"
                      type="primary"
                      size="small"
                      icon={<UnlockOutlined />}
                      onClick={() => openForceModal(d)}
                    >
                      Close this day
                    </Button>
                  ) : null,
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{dayjs(d.businessDate).format('DD/MM/YYYY')}</Text>
                      <Tag>{(d.status || '').replace(/_/g, ' ')}</Tag>
                    </Space>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Expected closing cash: ₹{fmt(d.expectedCash)}
                      {d.openedBy ? ` · opened by ${d.openedBy}` : ''}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {(loading && !eodState) && <Spin style={{ display: 'block', marginTop: 40 }} />}

      {eodState && (
        <>
          {/* ── Progress steps ── */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Steps
              current={currentStep}
              size="small"
              items={[
                { title: 'Day Open', description: 'Transactions recorded' },
                { title: 'Cash Verification', description: 'Count denominations' },
                { title: 'Awaiting Approval', description: 'Submitted to HO' },
                { title: 'Closed', description: 'HO approved' },
              ]}
            />
          </Card>

          {/* ── Already closed / pending approval ── */}
          {dayStatus === DAY_STATUS.CLOSED && (
            <Result
              status="success"
              title="Business Day Closed"
              subTitle={`Approved and closed${eodState.businessDay?.closedAt ? ` on ${dayjs(eodState.businessDay.closedAt).format('DD/MM/YYYY HH:mm')}` : ''}`}
            />
          )}

          {dayStatus === DAY_STATUS.PENDING_CHECKER_APPROVAL && (
            <Result
              icon={<ClockCircleOutlined style={{ color: '#1677ff' }} />}
              title="Submitted for Approval"
              subTitle={
                eodState.businessDay?.submittedForApprovalAt
                  ? `Submitted at ${dayjs(eodState.businessDay.submittedForApprovalAt).format('HH:mm DD/MM/YYYY')}`
                  : 'Awaiting approval'
              }
              extra={
                <Space direction="vertical" size={8}>
                  <Space>
                    <Tag color="blue">Physical Cash: ₹{fmt(eodState.businessDay?.denominationCount?.totalPhysicalCash || 0)}</Tag>
                    <Tag color="green">Difference: ₹0</Tag>
                  </Space>
                  {eodState.pendingApproval && (
                    <Text type="secondary">
                      Pending with <strong>{eodState.pendingApproval.stepName || 'approver'}</strong>
                      {eodState.pendingApproval.approvers?.length > 0 && (
                        <>: {eodState.pendingApproval.approvers.map(a => a.name).filter(Boolean).join(', ')}</>
                      )}
                    </Text>
                  )}
                </Space>
              }
            />
          )}

          {/* ── Active EOD sections ── */}
          {[DAY_STATUS.OPEN, DAY_STATUS.REOPENED, 'NO_DAY', DAY_STATUS.PENDING_CASH_VERIFICATION].includes(dayStatus) && (
            <>
              {/* Cash Summary */}
              <Card
                size="small"
                title="Cash Summary"
                style={{ marginBottom: 16 }}
                extra={
                  (dayStatus === 'NO_DAY' || dayStatus === DAY_STATUS.OPEN || dayStatus === DAY_STATUS.REOPENED) && (
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={handleInitiate}
                      loading={actionLoading}
                      disabled={isHO && !selectedBranchId}
                    >
                      Initiate EOD
                    </Button>
                  )
                }
              >
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <Statistic title="Opening Cash" value={fmt(eodState.summary.openingCash)} prefix="₹" valueStyle={{ fontSize: 15 }} />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic title="Total Cash In" value={fmt(eodState.summary.totalCashIn)} prefix="₹" valueStyle={{ color: '#389e0d', fontSize: 15 }} />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic title="Total Cash Out" value={fmt(eodState.summary.totalCashOut)} prefix="₹" valueStyle={{ color: '#cf1322', fontSize: 15 }} />
                  </Col>
                  {showExpectedClosingCash && (
                    <Col xs={12} sm={6}>
                      <Statistic
                        title={
                          <Space size={4}>
                            <span>Expected Closing Cash</span>
                            {lastUpdated && isPendingVerification && (
                              <Text type="secondary" style={{ fontSize: 11, fontWeight: 'normal' }}>
                                · updated {dayjs(lastUpdated).format('HH:mm:ss')}
                              </Text>
                            )}
                          </Space>
                        }
                        value={fmt(expectedCash)}
                        prefix="₹"
                        valueStyle={{ fontWeight: 'bold', fontSize: 15 }}
                      />
                    </Col>
                  )}
                </Row>
                {dayStatus !== 'NO_DAY' && (
                  <div style={{ marginTop: 8 }}>
                    <Tag>Status: <strong>{dayStatus.replace(/_/g, ' ')}</strong></Tag>
                    {eodState.businessDay?.openedAt && (
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        Opened at {dayjs(eodState.businessDay.openedAt).format('HH:mm')}
                      </Text>
                    )}
                  </div>
                )}
              </Card>

              {/* Denomination Count (only visible once EOD is initiated) */}
              {isPendingVerification && (
                <Card
                  size="small"
                  title="Denomination Count"
                  style={{ marginBottom: 16 }}
                  extra={
                    <Space>
                      <Button
                        icon={<SaveOutlined />}
                        onClick={handleSaveDenomination}
                        loading={actionLoading}
                        disabled={!denominationEntered}
                      >
                        Save Counts
                      </Button>
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleSubmit}
                        loading={actionLoading}
                        disabled={!canSubmit}
                        title={!canSubmit ? (denominationEntered ? 'Cash difference must be ₹0 to submit' : 'Enter denomination counts first') : ''}
                      >
                        Submit for Approval
                      </Button>
                    </Space>
                  }
                >
                  <Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 12 }}>
                    Count all physical cash in the branch drawer. The total must match the expected closing balance before you can submit for HO approval.
                  </Paragraph>
                  <DenominationCountForm
                    counts={counts}
                    onChange={setCounts}
                    disabled={!isEditable}
                    expectedCash={expectedCash}
                    hideExpectedCash={formHideExpectedCash}
                    hideDifference={formHideDifference}
                  />
                </Card>
              )}
            </>
          )}
        </>
      )}

      {/* ── Force-close override modal ── */}
      <Modal
        open={!!forceDay}
        title={
          <Space>
            <UnlockOutlined style={{ color: '#faad14' }} />
            <span>
              {bulkMode
                ? `Close all ${blockingDays.length} previous days`
                : `Close previous day — ${forceDay ? dayjs(forceDay.businessDate).format('DD/MM/YYYY') : ''}`}
            </span>
          </Space>
        }
        onCancel={closeForceModal}
        width={640}
        footer={[
          <Button key="cancel" onClick={closeForceModal}>Cancel</Button>,
          <Button
            key="confirm"
            type="primary"
            danger={forceDifference !== 0}
            icon={<UnlockOutlined />}
            loading={forceLoading}
            onClick={handleForceClose}
          >
            {bulkMode
              ? `Close all ${blockingDays.length} days`
              : (forceDifference === 0 ? 'Close Day' : `Close with ₹${fmt(Math.abs(forceDifference))} difference`)}
          </Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Override — this closes the day(s) directly, skipping checker approval."
          description={
            bulkMode
              ? `Count the physical cash currently in the drawer — it applies to the latest day (${forceDay ? dayjs(forceDay.businessDate).format('DD/MM/YYYY') : ''}), which carries any difference. The other ${blockingDays.length - 1} earlier day(s) auto-close at their own expected balance (₹0 difference).`
              : 'Count the physical cash for this day. If it does not match the expected closing cash, the difference is recorded on the day for audit.'
          }
        />
        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={8}>
            <Statistic title="Expected Closing" value={fmt(forceExpected)} prefix="₹" valueStyle={{ fontSize: 15 }} />
          </Col>
          <Col span={8}>
            <Statistic title="Physical (counted)" value={fmt(forcePhysical)} prefix="₹" valueStyle={{ fontSize: 15 }} />
          </Col>
          <Col span={8}>
            <Statistic
              title="Difference"
              value={fmt(forceDifference)}
              prefix="₹"
              valueStyle={{ fontSize: 15, color: forceDifference === 0 ? '#389e0d' : '#cf1322' }}
            />
          </Col>
        </Row>
        <DenominationCountForm
          counts={forceCounts}
          onChange={setForceCounts}
          expectedCash={forceExpected}
        />
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 13 }}>Reason for override <Text type="danger">*</Text></Text>
          <Input.TextArea
            rows={2}
            value={forceReason}
            onChange={(e) => setForceReason(e.target.value)}
            placeholder="e.g. Teller left without closing; cash verified physically by branch manager."
            style={{ marginTop: 6 }}
            maxLength={500}
          />
        </div>
      </Modal>
    </div>
  );
}
